// use-eve-memory — React-Wrapper für EVE-Memory + RAS. V1-Pendant: useEveMemory.js.
//
// Verantwortungen:
// 1. Memories, RAS-Rules, Global-RAS aus Backend laden (TTL-cached).
// 2. CRUD-Operationen (add/delete/clear) mit optimistischen Store-Updates.
// 3. System-Risk-Snapshots persistieren.
// 4. Memory-Extraction aus EVE-Antworten via LLM-Adapter.
// 5. Prompt-Context-Builder für EVE-System-Prompts.

import { useCallback, useMemo } from 'react';
import {
  addMemory as addMemoryApi,
  callEveLlm,
  clearMemories as clearMemoriesApi,
  clearRasRules as clearRasRulesApi,
  deleteGlobalRasRule as deleteGlobalRasRuleApi,
  deleteMemory as deleteMemoryApi,
  deleteRasRule as deleteRasRuleApi,
  listGlobalRasRules,
  listMemories,
  listRasRules,
  saveRasRule as saveRasRuleApi,
} from '@/lib/eve-memory-api';
import {
  formatFullMemoryContext,
  formatSystemRiskContext,
  parseAiExtractedRules,
  splitFactsAndRasRules,
} from '@/lib/eve-memory-context';
import {
  isDuplicateRasRule,
  validateRasRule,
} from '@/lib/ras-validation';
import {
  isCacheStale,
  useEveMemoryStore,
} from '@/stores/eve-memory-store';
import { selectAccessToken, useAuthStore } from '@/stores/auth-store';
import type {
  GlobalRasRule,
  MemoryEntry,
  RasRule,
  SystemRiskSnapshot,
} from '@/types/eve-memory';

export interface UseEveMemoryReturn {
  memories: MemoryEntry[];
  rasRules: RasRule[];
  globalRasRules: GlobalRasRule[];
  systemRisk: SystemRiskSnapshot | null;

  /** Lädt Memories vom Worker — respektiert 5min TTL es sei denn `force=true`. */
  loadMemories: (force?: boolean) => Promise<MemoryEntry[]>;
  loadRasRules: (force?: boolean) => Promise<RasRule[]>;
  loadGlobalRasRules: (force?: boolean) => Promise<GlobalRasRule[]>;

  addMemory: (fact: string) => Promise<MemoryEntry | null>;
  saveRasRule: (rule: string, triggerContext: string) => Promise<RasRule | null>;
  deleteMemory: (id: MemoryEntry['id']) => Promise<void>;
  deleteRasRule: (id: RasRule['id']) => Promise<void>;
  /** Löscht eine globale RAS-Regel (Backend owner-only). */
  deleteGlobalRasRule: (id: GlobalRasRule['id']) => Promise<void>;
  clearMemories: () => Promise<void>;
  clearRasRules: () => Promise<void>;

  saveSystemRisk: (snapshot: Omit<SystemRiskSnapshot, 'savedAt'>) => void;
  systemRiskContext: string | null;
  fullContext: (userMsg?: string) => string | null;

  /**
   * Extrahiert Memory + RAS aus einem User↔EVE Austausch via LLM-Adapter
   * und speichert sie. Spiegelt V1 `extractAndSaveMemory`.
   */
  extractAndSave: (userMsg: string, eveResponse: string) => Promise<void>;
}

const EXTRACT_PROMPT_TEMPLATE = (userMsg: string, eveResponse: string): string => `
Du analysierst ein Gespräch zwischen Dr. Upro (User) und EVE (KI-Analystin).
Extrahiere ALLES was langfristig relevant sein könnte — sei großzügig, nicht restriktiv:

KATEGORIE 1 — Persönliches:
- Persönliche Ereignisse, Befindlichkeiten, emotionale Zustände
- Trades, Positionen, Investitionsentscheidungen
- Präferenzen, Abneigungen, Gewohnheiten
- Pläne und Absichten

KATEGORIE 2 — Analytische Erkenntnisse & Marktbeobachtungen:
- Markt-Insights oder Thesen die Dr. Upro äußert
- Makro-Zusammenhänge die im Gespräch erkannt wurden
- Geopolitische Beobachtungen mit Marktrelevanz
- EVE's eigene Schlussfolgerungen die merkenswert sind

KATEGORIE 3 — Interaktionsmuster:
- Wie Dr. Upro denkt oder Informationen verknüpft
- Hinweise auf seinen Analyse-Stil oder Trading-Ansatz
- Wichtige Reibungspunkte oder Präzisierungen im Umgang mit EVE

KATEGORIE 4 — RAS-LEARNINGS (HÖCHSTE PRIORITÄT):
Format: "[RAS-REGEL] <die Denkregel in einem klaren, handlungsorientierten Satz>"

Gespräch:
USER: ${userMsg}
EVE: ${eveResponse}

Antworte NUR mit einer JSON-Liste von Strings.
RAS-Learnings mit Prefix [RAS-REGEL] immer zuerst.
`.trim();

export function useEveMemory(): UseEveMemoryReturn {
  const memories = useEveMemoryStore((s) => s.memories);
  const rasRules = useEveMemoryStore((s) => s.rasRules);
  const globalRasRules = useEveMemoryStore((s) => s.globalRasRules);
  const systemRisk = useEveMemoryStore((s) => s.systemRisk);
  const accessToken = useAuthStore(selectAccessToken);

  const systemRiskContext = useMemo(() => formatSystemRiskContext(systemRisk), [systemRisk]);

  const loadMemories = useCallback<UseEveMemoryReturn['loadMemories']>(
    async (force = false): Promise<MemoryEntry[]> => {
      const store = useEveMemoryStore.getState();
      if (!force && !isCacheStale(store.memoriesFetchedAt)) return store.memories;
      if (!accessToken) return store.memories;
      try {
        const entries = await listMemories(accessToken);
        store.setMemories(entries);
        return entries;
      } catch (e) {
        console.warn('[EVE Memory] loadMemories failed:', e instanceof Error ? e.message : e);
        return store.memories;
      }
    },
    [accessToken],
  );

  const loadRasRules = useCallback<UseEveMemoryReturn['loadRasRules']>(
    async (force = false): Promise<RasRule[]> => {
      const store = useEveMemoryStore.getState();
      if (!force && !isCacheStale(store.rasRulesFetchedAt)) return store.rasRules;
      if (!accessToken) return store.rasRules;
      try {
        const rules = await listRasRules(accessToken);
        store.setRasRules(rules);
        return rules;
      } catch (e) {
        console.warn('[EVE Memory] loadRasRules failed:', e instanceof Error ? e.message : e);
        return store.rasRules;
      }
    },
    [accessToken],
  );

  const loadGlobalRasRules = useCallback<UseEveMemoryReturn['loadGlobalRasRules']>(
    async (force = false): Promise<GlobalRasRule[]> => {
      const store = useEveMemoryStore.getState();
      if (!force && !isCacheStale(store.globalRasFetchedAt)) return store.globalRasRules;
      if (!accessToken) return store.globalRasRules;
      try {
        const rules = await listGlobalRasRules(accessToken);
        store.setGlobalRasRules(rules);
        return rules;
      } catch (e) {
        console.warn('[EVE Memory] loadGlobalRasRules failed:', e instanceof Error ? e.message : e);
        return store.globalRasRules;
      }
    },
    [accessToken],
  );

  const addMemory = useCallback<UseEveMemoryReturn['addMemory']>(
    async (fact: string): Promise<MemoryEntry | null> => {
      if (!accessToken) return null;
      try {
        const entry = await addMemoryApi({ fact }, accessToken);
        useEveMemoryStore.getState().prependMemory(entry);
        return entry;
      } catch (e) {
        console.warn('[EVE Memory] addMemory failed:', e instanceof Error ? e.message : e);
        return null;
      }
    },
    [accessToken],
  );

  const saveRasRule = useCallback<UseEveMemoryReturn['saveRasRule']>(
    async (rule: string, triggerContext: string): Promise<RasRule | null> => {
      if (!accessToken) return null;
      const guard = validateRasRule(rule);
      if (!guard.valid) {
        console.warn('[RAS] blocked:', guard.reason);
        return null;
      }
      const existing = useEveMemoryStore.getState().rasRules;
      if (isDuplicateRasRule(rule, existing)) return null;
      try {
        const saved = await saveRasRuleApi({ rule, trigger_context: triggerContext }, accessToken);
        useEveMemoryStore.getState().prependRasRule(saved);
        return saved;
      } catch (e) {
        console.warn('[RAS] saveRasRule failed:', e instanceof Error ? e.message : e);
        return null;
      }
    },
    [accessToken],
  );

  const deleteMemoryAction = useCallback<UseEveMemoryReturn['deleteMemory']>(
    async (id) => {
      if (!accessToken) return;
      try {
        await deleteMemoryApi(id, accessToken);
        useEveMemoryStore.getState().removeMemory(id);
      } catch (e) {
        console.warn('[EVE Memory] deleteMemory failed:', e instanceof Error ? e.message : e);
      }
    },
    [accessToken],
  );

  const deleteRasRuleAction = useCallback<UseEveMemoryReturn['deleteRasRule']>(
    async (id) => {
      if (!accessToken) return;
      try {
        await deleteRasRuleApi(id, accessToken);
        useEveMemoryStore.getState().removeRasRule(id);
      } catch (e) {
        console.warn('[RAS] deleteRasRule failed:', e instanceof Error ? e.message : e);
      }
    },
    [accessToken],
  );

  const deleteGlobalRasRuleAction = useCallback<UseEveMemoryReturn['deleteGlobalRasRule']>(
    async (id) => {
      if (!accessToken) return;
      try {
        await deleteGlobalRasRuleApi(id, accessToken);
        useEveMemoryStore.getState().removeGlobalRasRule(id);
      } catch (e) {
        console.warn('[RAS] deleteGlobalRasRule failed:', e instanceof Error ? e.message : e);
      }
    },
    [accessToken],
  );

  const clearMemoriesAction = useCallback<UseEveMemoryReturn['clearMemories']>(async () => {
    if (!accessToken) return;
    try {
      await clearMemoriesApi(accessToken);
      useEveMemoryStore.getState().setMemories([]);
    } catch (e) {
      console.warn('[EVE Memory] clearMemories failed:', e instanceof Error ? e.message : e);
    }
  }, [accessToken]);

  const clearRasRulesAction = useCallback<UseEveMemoryReturn['clearRasRules']>(async () => {
    if (!accessToken) return;
    try {
      await clearRasRulesApi(accessToken);
      useEveMemoryStore.getState().setRasRules([]);
    } catch (e) {
      console.warn('[RAS] clearRasRules failed:', e instanceof Error ? e.message : e);
    }
  }, [accessToken]);

  const saveSystemRisk = useCallback<UseEveMemoryReturn['saveSystemRisk']>((snapshot) => {
    useEveMemoryStore.getState().setSystemRisk({
      ...snapshot,
      savedAt: new Date().toISOString(),
    });
  }, []);

  const fullContext = useCallback<UseEveMemoryReturn['fullContext']>(
    (userMsg = '') =>
      formatFullMemoryContext({
        memories: useEveMemoryStore.getState().memories,
        rasRules: useEveMemoryStore.getState().rasRules,
        globalRasRules: useEveMemoryStore.getState().globalRasRules,
        userMsg,
      }),
    [],
  );

  const extractAndSave = useCallback<UseEveMemoryReturn['extractAndSave']>(
    async (userMsg, eveResponse) => {
      if (!accessToken) return;
      try {
        const raw = await callEveLlm(
          EXTRACT_PROMPT_TEMPLATE(userMsg, eveResponse),
          accessToken,
        );
        if (!raw) return;
        const facts = parseAiExtractedRules(raw);
        const { rules, plainFacts } = splitFactsAndRasRules(facts);
        const existing = useEveMemoryStore.getState().rasRules;

        for (const rule of rules) {
          const guard = validateRasRule(rule);
          if (!guard.valid) continue;
          if (isDuplicateRasRule(rule, existing)) continue;
          await saveRasRule(rule, `User: "${userMsg.slice(0, 100)}…" | EVE: "${eveResponse.slice(0, 100)}…"`);
        }
        for (const fact of plainFacts) {
          await addMemory(fact);
        }
      } catch (e) {
        console.warn('[EVE Memory] extractAndSave failed:', e instanceof Error ? e.message : e);
      }
    },
    [accessToken, addMemory, saveRasRule],
  );

  return {
    memories,
    rasRules,
    globalRasRules,
    systemRisk,
    loadMemories,
    loadRasRules,
    loadGlobalRasRules,
    addMemory,
    saveRasRule,
    deleteMemory: deleteMemoryAction,
    deleteRasRule: deleteRasRuleAction,
    deleteGlobalRasRule: deleteGlobalRasRuleAction,
    clearMemories: clearMemoriesAction,
    clearRasRules: clearRasRulesAction,
    saveSystemRisk,
    systemRiskContext,
    fullContext,
    extractAndSave,
  };
}
